import { NextRequest, NextResponse } from 'next/server'

import { User } from './types/user.type'
import { isStoragePrimaryUser } from './lib/storagePrimaryUtils'
import { fetchCurrentUser } from './lib/serverBackendApiUrl'
import {
  RETURN_PATH_PARAM,
  SESSION_EXPIRED_PARAM,
  SESSION_EXPIRED_VALUE,
} from './constants/authRedirect'
import { isSafeReturnPath } from './lib/authReturnPath'
import { ResponseCode } from './types/response.type'
import {
  buildDesktopLoopbackCallback,
  isDesktopAuthorizeReturnPath,
} from './lib/desktopOAuthReturnPath'

// Cookie name for passing user data from middleware to client
export const USER_DATA_COOKIE_NAME = '__user_data'

// Helper function to set user data cookie on response
function setUserDataCookie(response: NextResponse, user: User): NextResponse {
  // Store user data in a cookie that client-side can read
  // The cookie is short-lived (10 seconds) - just enough time for the client to read it
  const userData = JSON.stringify(user)

  response.cookies.set(USER_DATA_COOKIE_NAME, userData, {
    path: '/',
    maxAge: 10, // 10 seconds - short-lived, just for initial page load
    sameSite: 'strict',
    secure: process.env.NODE_ENV === 'production',
    httpOnly: false, // Must be false so client JS can read it
  })

  return response
}

// Helper function to forward Set-Cookie headers from backend response
function forwardBackendCookies(response: NextResponse, backendCookies: string[]): NextResponse {
  for (const cookie of backendCookies) {
    response.headers.append('Set-Cookie', cookie)
  }
  return response
}

function readBackendCookies(headers: Headers): string[] {
  const getSetCookie = (headers as Headers & { getSetCookie?: () => string[] }).getSetCookie
  if (typeof getSetCookie === 'function') {
    const cookies = getSetCookie.call(headers)
    if (cookies.length > 0) return cookies
  }

  const combined = headers.get('set-cookie')
  return combined ? combined.split(/,(?=\s*[^=;,\s]+=)/).map((cookie) => cookie.trimStart()) : []
}

// Helper function to create NextResponse.next() with user data cookie
function nextWithUserData(user: User, backendCookies: string[] = []): NextResponse {
  const response = NextResponse.next()
  setUserDataCookie(response, user)
  return forwardBackendCookies(response, backendCookies)
}

// 쿠키 이름이 환경별로 다르다 (`linq-alpha-jwt-key` / `linq-alpha-stag-jwt-key`) — 패턴으로 본다.
const AUTH_COOKIE_NAME_PATTERN = /(jwt|refresh)-key$/

/**
 * 인증 토큰 쿠키를 **가지고 왔는지**. 만료된 세션과 애초에 로그인한 적 없는 방문자를
 * 구분하는 유일한 신호다. `validateUserToken` 은 둘 다 `valid: false` 로 뭉뚱그린다.
 */
function hadAuthCookie(request: NextRequest): boolean {
  return request.cookies.getAll().some((c) => AUTH_COOKIE_NAME_PATTERN.test(c.name))
}

// 인증 실패 → 로그인 페이지로. `from` 에는 쿼리까지 포함한 원래 URL을 담는다
// (conversation_id 같은 컨텍스트가 있어야 복귀가 의미 있다).
//
// ⚠️ `from` 을 **소비**하는 곳은 로그인 성공 시점(`useAuth.signInSession`) 한 곳뿐이어야 한다.
// 미들웨어가 이 값을 읽어 앱으로 되돌려보내면, 인터셉터가 다시 sign_in으로 보내는
// 무한 리다이렉트 고리가 생긴다. 여기서는 쓰기만 한다.
function redirectToSignIn(request: NextRequest): NextResponse {
  const originalPath = `${request.nextUrl.pathname}${request.nextUrl.search}`
  const url = request.nextUrl.clone()
  url.pathname = '/users/sign_in'
  // clone은 원래 페이지의 쿼리를 그대로 들고 온다 — 비우지 않으면 sign_in URL에
  // `?tab=all&from=…` 처럼 무관한 파라미터가 샌다. 원래 쿼리는 `from` 안에만 담는다.
  url.search = ''
  url.searchParams.set(RETURN_PATH_PARAM, originalPath)
  // 세션 만료 토스트는 **실제로 세션이 있었던** 사용자에게만 띄운다. 토큰 쿠키조차 없이
  // 보호된 URL로 들어온 익명 방문자에게 "세션이 만료되었습니다"는 사실이 아니고,
  // 앱 루트(`/`)까지 matcher에 걸려 있어 첫 방문자 전원이 에러 토스트를 보게 된다.
  if (hadAuthCookie(request)) {
    url.searchParams.set(SESSION_EXPIRED_PARAM, SESSION_EXPIRED_VALUE)
  }
  return NextResponse.redirect(url)
}

function desktopAuthorizeReturnPath(request: NextRequest): string | null {
  const currentPath = `${request.nextUrl.pathname}${request.nextUrl.search}`
  if (isDesktopAuthorizeReturnPath(currentPath)) return currentPath

  const from = request.nextUrl.searchParams.getAll(RETURN_PATH_PARAM)
  if (from.length === 1 && isDesktopAuthorizeReturnPath(from[0])) return from[0]
  return null
}

function redirectToUserStatus(
  request: NextRequest,
  redirectPath: string,
  backendCookies: string[]
): NextResponse {
  const pendingDesktopPath = desktopAuthorizeReturnPath(request)
  const url = request.nextUrl.clone()
  url.pathname = redirectPath
  if (pendingDesktopPath) {
    url.search = ''
    url.searchParams.set(RETURN_PATH_PARAM, pendingDesktopPath)
    return forwardBackendCookies(NextResponse.redirect(url), backendCookies)
  }
  // Carry the destination through the investment profile gate only. The flag turns on for a
  // whole org at once, so without this everyone bounced mid-task lands on `/` the same day.
  // Scoped to this one route: the other status pages do not read `from` for a return.
  if (redirectPath === INVESTMENT_PROFILE_GATE_PATH) {
    const origin = `${request.nextUrl.pathname}${request.nextUrl.search}`
    url.search = ''
    if (isSafeReturnPath(origin) && !USER_STATUS_ROUTES.includes(request.nextUrl.pathname)) {
      url.searchParams.set(RETURN_PATH_PARAM, origin)
    }
  }
  return forwardBackendCookies(NextResponse.redirect(url), backendCookies)
}

function redirectToDesktopAuthorize(request: NextRequest, returnPath: string): NextResponse {
  return NextResponse.redirect(new URL(returnPath, request.nextUrl.origin))
}

// 인증이 필요없는 페이지
export const PUBLIC_ROUTES = [
  '/brief_agent/feedback',
  '/inbound_emails/[token]/answer',
  '/messages/[id]/answer',
  '/references/[id]/document',
  '/rms_references/[id]',
  '/earnings_events/[id]/transcript',
  '/earnings_events/[id]/press_release',
  '/teamjoin/[invitation_token]',
  '/teamjoin/[invitation_token]/email',
  '/404',
  '/documents/[id]',
  '/rms/documents',
  '/rms/documents_by_chunk',
  '/rms/viewer',
  '/meetings/edit',
  '/users/email/confirm',
  // Onboarding holding screens. They are public because the server issues no session until
  // onboarding is done — there is nothing to authenticate. See linq-alpha-server#3858.
  '/users/email/verify',
  '/users/pending',
  '/references',
  '/references/[id]/document',
  '/users/forgot_password',
  '/users/reset_password',
  '/rms_v2/sharepoint-consent',
  '/robots.txt',
  '/sitemap.xml',
]
// 로그인 상태로는 접속 불가능한 페이지
const NO_AUTH_ROUTES = [
  '/users/oauth/google',
  '/users/oauth/cognito',
  '/users/sign_in',
  '/users/sign_up',
  '/users/sso',
]

// 사용자 상태 관련 페이지들 (로그인된 사용자가 접근 가능. 다만 다른 기능을 이용하기위해서는 해당 페이지들을 패스 해야함.)
// Must stay 1:1 with the paths getRedirectPath returns.
const INVESTMENT_PROFILE_GATE_PATH = '/onboarding/investment-profile'

const USER_STATUS_ROUTES = [
  '/users/set_initial_password',
  '/users/additional_info',
  INVESTMENT_PROFILE_GATE_PATH,
]

// Authenticated users may reach these routes before completing the investment profile.
// This does not bypass sign-in, password changes, or required additional information.
const INVESTMENT_PROFILE_GATE_ALLOWLIST = ['/desktop/login']

// 동적 라우트 패턴을 정규식으로 변환하는 함수
function createRouteRegex(route: string): RegExp {
  // [id], [invitation_token] 등을 정규식 패턴으로 변환
  const regexPattern = route.replace(/\[([^\]]+)\]/g, '([^/]+)') // [id] → ([^/]+)

  return new RegExp(`^${regexPattern}$`)
}

// 경로가 라우트 패턴과 일치하는지 확인
function matchesRoute(pathname: string, routes: string[]): boolean {
  return routes.some((route) => {
    if (route.includes('[')) {
      // 동적 라우트인 경우 정규식으로 매칭
      const regex = createRouteRegex(route)
      return regex.test(pathname)
    } else {
      // 정적 라우트인 경우 정확히 일치
      return pathname === route
    }
  })
}

// 인증이 필요없는 페이지인지 확인
export function isPublicRoute(pathname: string): boolean {
  return matchesRoute(pathname, PUBLIC_ROUTES)
}

// 로그인 상태로는 접속 불가능한 페이지인지 확인
export function isNoAuthRoute(pathname: string): boolean {
  return matchesRoute(pathname, NO_AUTH_ROUTES)
}

// 사용자 상태 관련 페이지인지 확인
// No longer `/users/*` only — the `/onboarding/*` gate lives here too.
function isUserStatusRoute(pathname: string): boolean {
  return matchesRoute(pathname, USER_STATUS_ROUTES)
}

function isInvestmentProfileGateAllowed(path: string): boolean {
  return matchesRoute(path.split('?')[0], INVESTMENT_PROFILE_GATE_ALLOWLIST)
}

// 사용자 상태에 따른 리다이렉트 대상 페이지 결정
//
// Only active users reach here: the server issues no session for Draft/Pending and 403s
// `/users/me` for them, so those two are routed from the sign-in / OAuth error codes instead
// of from a session. See linq-alpha-server#3858.
function getRedirectPath(user: User, requestedPath: string): string | null {
  if (user.need_password_change) {
    return '/users/set_initial_password'
  }
  if (user.need_additional_info) {
    return '/users/additional_info'
  }
  // Must stay last: gating earlier would lock out password/additional-info completion.
  if (user.need_investment_profile && !isInvestmentProfileGateAllowed(requestedPath)) {
    return INVESTMENT_PROFILE_GATE_PATH
  }

  return null // 모든 상태가 정상이면 null 반환
}

type UserTokenValidation =
  | { kind: 'authenticated'; user: User; backendCookies: string[] }
  | { kind: 'not_enabled'; message: string; backendCookies: string[] }
  | { kind: 'unauthenticated'; backendCookies: [] }

// /users/me API 호출
async function validateUserToken(request: NextRequest): Promise<UserTokenValidation> {
  try {
    // Bounded + internal-then-public failover. See lib/serverBackendApiUrl.
    const response = await fetchCurrentUser({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Cookie: request.headers.get('cookie') || '',
      'X-Forwarded-Host': request.headers.get('host') || '',
    })
    if (!response) {
      return { kind: 'unauthenticated', backendCookies: [] }
    }

    // 백엔드가 token refresh 시 Set-Cookie로 새 access token을 보냄 → 브라우저에 전달해야 함
    const backendCookies = readBackendCookies(response.headers)

    if (response.ok) {
      const data = await response.json()
      if (data?.response) {
        return { kind: 'authenticated', user: data.response, backendCookies }
      }
      return { kind: 'unauthenticated', backendCookies: [] }
    }

    if (response.status === 403) {
      try {
        const data = await response.json()
        if (data?.code === ResponseCode.NOT_ENABLED) {
          return {
            kind: 'not_enabled',
            message: typeof data.message === 'string' ? data.message : 'Desktop access denied',
            backendCookies,
          }
        }
      } catch {
        // Unknown non-JSON failures stay on the ordinary unauthenticated path.
      }
    }

    return { kind: 'unauthenticated', backendCookies: [] }
  } catch (error) {
    return { kind: 'unauthenticated', backendCookies: [] }
  }
}

export async function middleware(request: NextRequest) {
  const { pathname } = request.nextUrl

  // 1. PUBLIC_ROUTES 페이지 -> 그냥 보여주기
  if (isPublicRoute(pathname)) {
    return NextResponse.next()
  }

  // 2. NO_AUTH_ROUTES 페이지
  if (isNoAuthRoute(pathname)) {
    const auth = await validateUserToken(request)

    // 로그인 안한 사용자라면, 그냥 보여주기
    if (auth.kind !== 'authenticated') {
      return NextResponse.next()
    }

    // 로그인 한 사용자라면, 응답값 대조하여 리다이렉트
    if (auth.user) {
      const pendingDesktopPath = desktopAuthorizeReturnPath(request)
      const redirectPath = getRedirectPath(auth.user, pendingDesktopPath ?? pathname)

      if (redirectPath) {
        // 무한 리다이렉션 방지: 현재 경로가 이미 리다이렉트 대상 경로라면 그냥 보여주기
        if (pathname === redirectPath) {
          return NextResponse.next()
        }
        return redirectToUserStatus(request, redirectPath, auth.backendCookies)
      }

      if (pendingDesktopPath) {
        return forwardBackendCookies(
          redirectToDesktopAuthorize(request, pendingDesktopPath),
          auth.backendCookies
        )
      }

      // 모든 상태가 정상이라면 메인페이지로 redirect
      const url = request.nextUrl.clone()
      url.pathname = '/'
      return NextResponse.redirect(url)
    }

    // 토큰이 유효하지 않은 경우 그냥 페이지 보여주기
    return NextResponse.next()
  }

  // 3. USER_STATUS_ROUTES 페이지 (사용자 상태 관련 페이지)
  if (isUserStatusRoute(pathname)) {
    const auth = await validateUserToken(request)

    if (auth.kind !== 'authenticated') {
      // 현재 경로가 이미 sign_in이면 추가 리다이렉트 방지
      if (pathname === '/users/sign_in') {
        return NextResponse.next()
      }
      // 토큰이 유효하지 않으면 로그인 페이지로 리다이렉트
      return redirectToSignIn(request)
    }

    if (auth.user) {
      const pendingDesktopPath = desktopAuthorizeReturnPath(request)
      const redirectPath = getRedirectPath(auth.user, pendingDesktopPath ?? pathname)

      if (redirectPath) {
        // 현재 경로가 올바른 상태 페이지인지 확인
        if (pathname === redirectPath) {
          return nextWithUserData(auth.user, auth.backendCookies)
        }
        // 다른 상태 페이지로 리다이렉트 필요
        return redirectToUserStatus(request, redirectPath, auth.backendCookies)
      }

      if (pendingDesktopPath) {
        return forwardBackendCookies(
          redirectToDesktopAuthorize(request, pendingDesktopPath),
          auth.backendCookies
        )
      }

      // 모든 상태가 정상이라면 메인페이지로 redirect
      const url = request.nextUrl.clone()
      url.pathname = '/'
      return forwardBackendCookies(NextResponse.redirect(url), auth.backendCookies)
    }

    return NextResponse.next()
  }

  // 4. 나머지 페이지들 (보호된 페이지)

  // Allow onboarding page to bypass middleware redirects in development mode only
  const isOnboardingBypass =
    pathname === '/earnings_management/onboarding' && process.env.NODE_ENV === 'development'
  if (isOnboardingBypass) {
    return NextResponse.next()
  }

  const auth = await validateUserToken(request)
  const pendingDesktopPath = desktopAuthorizeReturnPath(request)

  if (auth.kind === 'not_enabled' && pendingDesktopPath) {
    const callback = buildDesktopLoopbackCallback(pendingDesktopPath, {
      error: 'access_denied',
      errorDescription: auth.message,
      errorSource: 'authorization_server',
    })
    if (callback) {
      return forwardBackendCookies(NextResponse.redirect(callback), auth.backendCookies)
    }
  }

  if (auth.kind !== 'authenticated') {
    // 현재 경로가 이미 sign_in이면 추가 리다이렉트 방지
    if (pathname === '/users/sign_in') {
      return NextResponse.next()
    }
    // 토큰이 유효하지 않으면 로그인 페이지로 리다이렉트
    return redirectToSignIn(request)
  }

  if (auth.user) {
    const redirectPath = getRedirectPath(auth.user, pathname)

    if (redirectPath && !isOnboardingBypass && pathname !== redirectPath) {
      // 사용자 상태가 완료되지 않았다면 해당 페이지로 리다이렉트
      return redirectToUserStatus(request, redirectPath, auth.backendCookies)
    }

    // '/' 접근 시 리다이렉트 (getServerSideProps보다 먼저 실행됨)
    // Storage Primary 유저: / → /rms_v2
    // 일반 유저: / → /analytics (query params가 있으면 /tfs_search로 - getServerSideProps에서 처리)
    if (pathname === '/' && !request.nextUrl.searchParams.toString()) {
      if (isStoragePrimaryUser(auth.user.organization?.id, auth.user.group)) {
        const url = request.nextUrl.clone()
        url.pathname = '/rms_v2'
        return forwardBackendCookies(NextResponse.redirect(url), auth.backendCookies)
      }
    }

    // 모든 상태가 정상이라면 페이지 보여주기 (with user data cookie)
    return nextWithUserData(auth.user, auth.backendCookies)
  }

  // 유효한 토큰이 있는 경우 요청 계속 진행
  return NextResponse.next()
}

// Files under `public/` are not covered by the exclusions below — they do not sit under
// `_next/static` — so they run through this middleware, and anything outside PUBLIC_ROUTES
// gets a 307 to sign_in on an anonymous request. Correct for a page, fatal for an asset the
// browser loads as a script: the script tag receives login HTML instead.
//
// The pdf.js worker landed exactly there. Moving it off unpkg to self-hosting put
// `/pdf.worker.min.mjs` behind the middleware, so opening `/rms/viewer` — a PUBLIC_ROUTES
// page — while logged out redirected the worker request, pdf.js fell back to a fake worker,
// and the viewer showed nothing but "Failed to load PDF file."
//
// Excluded from the matcher rather than added to PUBLIC_ROUTES. (1) `cmaps/` is a directory,
// and PUBLIC_ROUTES matches an exact string or a `[param]` segment, so it cannot cover the
// files underneath. (2) There is no reason to spend a `/users/me` roundtrip on every asset
// request.
//
// Only the two paths below open up, not `public/` in general — a new asset fetched at
// runtime reproduces the same failure. Do not widen this into a blanket extension exclusion
// (`.*\..*` and the like): protected routes such as `/documents/[id]` take an id straight
// from the URL, so an id containing a dot would slip past auth. The dots are escaped and
// `cmaps` carries a trailing slash for the same reason — unescaped, a dot is a wildcard, and
// neighbours like `/pdf_worker_min_mjs` or `/cmapsFOO` would leave the auth boundary for good.
export const config = {
  matcher: [
    /*
     * Match all request paths except for the ones starting with:
     * - api (API routes)
     * - _next/static (static files)
     * - _next/image (image optimization files)
     * - favicon.ico (favicon file)
     * - pdf.worker.min.mjs, cmaps/ (pdf.js assets copied into public/ by the
     *   prebuild step — see scripts/copy-pdfjs-assets.mjs)
     */
    '/((?!api|_next/static|_next/image|favicon.ico|.well-known|pdf\\.worker\\.min\\.mjs|cmaps/).*)',
  ],
}
